/* eslint-disable functional/type-declaration-immutability -- this is a special case of ng 15 aliases that immutability isn't worth my time */
import { OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { Destroyable$ } from 'webcommon/shared/ap-base-class-ng1520';
import { takeUntil } from 'rxjs/operators';

// our own lightweight signal, before we migrate to a version of angular that supports signals
// handles Destroyable$
// todo: replace the simple usages of behaviorsubject in components with this.
export function apSignal$<T>(destroyable: Destroyable$, initialValue: T): ApWritableSignal$<T> {
  const value$ = new ApWritableSignal$_Impl<T>(initialValue);
  destroyable.onDestroy$.subscribe(() => {
    // clean-up subject
    value$.complete();
  });
  return value$;
}

// same as apSignal$, but defaults to undefined as initial value
export function apOptionalSignal$<T>(destroyable: Destroyable$, initialValue: T | undefined = undefined): ApWritableSignal$<T | undefined> {
  return apSignal$<T | undefined>(destroyable, initialValue);
}

// similar to createEventEmitterFromSource()
// returns a readonly Observable that gets its values from a source$
export function apSignalFromSource$<T>(destroyable: Destroyable$, source$: Observable<T>): ApSignal$<T> {
  const value$ = new Subject<T>();
  emitTo(destroyable, source$, value$);
  destroyable.onDestroy$.subscribe(() => {
    // clean-up subject
    value$.complete();
  });
  return value$;
}

// an interface that is close to Signal<T> in ng 20, so we can more easily migrate to it
export interface ApSignal$<T> extends Observable<T> {}

// an interface that is close to WritableSignal<T> in ng 20, so we can more easily migrate to it
// The main difference is that this is still rxjs, so you will use async pipe, instead of calling it like signal function
export interface ApWritableSignal$<T> extends ApSignal$<T> {
  // todo: do i also need to expose complete or error functions??

  /**
   * Directly set the signal to a new value, and notify any dependents.
   */
  set(value: T): void;
  /**
   * Update the value of the signal based on its current value, and
   * notify any dependents.
   */
  update(updateFn: (value: T) => T): void;
  /**
   * Returns a readonly version of this signal. Readonly signals can be accessed to read their value
   * but can't be changed using set or update methods. The readonly signals do _not_ have
   * any built-in mechanism that would prevent deep-mutation of their value.
   */
  asReadonly(): ApSignal$<T>;

  // added to be compatible with value of BehaviorSubject, instead of invoking this whole thing as a function, like an actual signal
  // the value property in BehaviorSubject just calls this internally anyways
  // migration: signal.getValue() to signal()
  // migration: signal | async to signal()
  // there would be case-by-case migrations for other stuff, like to use computed(), effect(), etc.
  getValue(): T;
}

export class ApWritableSignal$_Impl<T> extends BehaviorSubject<T> implements ApWritableSignal$<T> {
  set(value: T): void {
    super.next(value);
  }

  update(updateFn: (value: T) => T): void {
    super.next(updateFn(super.value));
  }

  asReadonly(): ApSignal$<T> {
    return super.asObservable();
  }
}

// todo: add tests
// emit to a Subject, and handle Destroyable$
export function emitTo<T>(destroyable: Destroyable$, source$: Observable<T>, output: Subject<T>): void {
  source$.pipe(takeUntil(destroyable.onDestroy$)).subscribe((value) => {
    output.next(value);
  });
}

// manage the single subscriptions on @Inputs and stuff without duplicating this code pattern everywhere
export class SingleSub {
  private _singleSub?: Subscription;

  constructor(
    private destroyable: Destroyable$,
  ) {
    this.destroyable.onDestroy$.subscribe(() => {
      this.stop();
    })
  }

  static create(...args: ConstructorParameters<typeof SingleSub>): SingleSub {
    return new SingleSub(...args);
  }

  start(newSub: Subscription): void {
    this._singleSub?.unsubscribe();
    this._singleSub = newSub;
  }

  stop(): void {
    this._singleSub?.unsubscribe();
    this._singleSub = undefined;
  }
}

// This is useful to convert a regular value to ApSignal$
// It seems like it can't inherit from current version of ApBaseClass, because ApBaseClass has @Directive(), and this is a pipe
@Pipe({
  name: 'toApSignal',
  pure: true,
  standalone: true,
})
export class ToApSignalPipe<TValue> implements PipeTransform, OnDestroy {
  private value$ = new ReplaySubject<TValue>(1);

  transform(value: TValue): ApSignal$<TValue> {
    this.value$.next(value);
    return this.value$;
  }

  ngOnDestroy() {
    this.value$.complete();
  }
}
